import { useEffect, useRef, useState, useCallback } from "react";
import api, { downloadUrl, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import logger from "../../lib/logger";
import { Plus, FileText, Upload, Trash2, Download, X, ChevronDown, ChevronUp } from "lucide-react";

export default function ProjectsTab({ courseId }) {
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openSubs, setOpenSubs] = useState({});
  const [subMap, setSubMap] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}/projects`);
      setProjects(res.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/projects", { course_id: courseId, ...form });
      setShowNew(false); setForm({ title: "", description: "", due_date: "" });
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const submitFile = async (pid, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${pid}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      fileEl.value = "";
      load();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${pid}`); load(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleSubs = async (pid) => {
    const open = !openSubs[pid];
    setOpenSubs((s) => ({ ...s, [pid]: open }));
    if (open) {
      try {
        const res = await api.get(`/projects/${pid}/submissions`);
        setSubMap((s) => ({ ...s, [pid]: res.data }));
      } catch (e) {
        logger.error("Failed to load project submissions:", e);
      }
    }
  };

  const gradeSub = async (subId, pid) => {
    const grade = parseFloat(window.prompt("Grade (0-100):", "85"));
    if (isNaN(grade)) return;
    const feedback = window.prompt("Feedback (optional):", "") || "";
    try {
      await api.post(`/project-submissions/${subId}/grade`, { grade, feedback });
      const res = await api.get(`/projects/${pid}/submissions`);
      setSubMap((s) => ({ ...s, [pid]: res.data }));
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6">
      {isInstructor && (
        <button data-testid="new-project-btn" onClick={() => setShowNew(true)} className="nb-btn nb-btn-mint flex items-center gap-2"><Plus className="w-4 h-4" /> New project</button>
      )}

      {projects.length === 0 && <div className="nb-card-static p-5 text-sm text-[var(--muted-text)]">No projects yet.</div>}

      {projects.map((p) => (
        <div key={p.id} data-testid={`project-${p.id}`} className="nb-card-static p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-xl font-bold">{p.title}</h3>
              {p.due_date && <p className="text-xs text-[var(--muted-text)] mt-1">Due {p.due_date}</p>}
              <p className="text-sm mt-2">{p.description}</p>
            </div>
            {isInstructor && (
              <button data-testid={`delete-project-${p.id}`} onClick={() => remove(p.id)} className="nb-btn nb-btn-white text-sm"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>

          {!isInstructor && (
            <div className="mt-4 border-t-2 border-[var(--gold)]/10 pt-4">
              {p.submission ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <p className="font-semibold">Your submission: {p.submission.filename}</p>
                    <p className="text-xs text-[var(--muted-text)]">Submitted {new Date(p.submission.submitted_at).toLocaleString()}</p>
                    {p.submission.graded && (
                      <p className="mt-1 text-sm"><span className="tag tag-mint">Grade: {p.submission.grade}/100</span> {p.submission.feedback && <span className="ml-2 italic">&ldquo;{p.submission.feedback}&rdquo;</span>}</p>
                    )}
                  </div>
                  <label className="nb-btn nb-btn-yellow text-sm cursor-pointer flex items-center gap-2">
                    <Upload className="w-4 h-4" /> Replace
                    <input data-testid={`project-resubmit-${p.id}`} type="file" className="hidden" onChange={(e) => submitFile(p.id, e.target)} />
                  </label>
                </div>
              ) : (
                <label className="nb-btn nb-btn-yellow text-sm cursor-pointer inline-flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Submit your project
                  <input data-testid={`project-submit-${p.id}`} type="file" className="hidden" onChange={(e) => submitFile(p.id, e.target)} />
                </label>
              )}
            </div>
          )}

          {isInstructor && (
            <div className="mt-4 border-t-2 border-[var(--gold)]/10 pt-3">
              <button data-testid={`toggle-subs-${p.id}`} onClick={() => toggleSubs(p.id)} className="text-sm font-semibold flex items-center gap-1">
                Submissions {openSubs[p.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {openSubs[p.id] && (
                <ul className="mt-3 space-y-2">
                  {(subMap[p.id] || []).length === 0 && <li className="text-sm text-[var(--muted-text)]">No submissions yet.</li>}
                  {(subMap[p.id] || []).map((s) => (
                    <li key={s.id} className="flex items-center justify-between border-2 border-[var(--gold)] rounded-lg px-3 py-2 bg-[var(--paper)]">
                      <div className="text-sm">
                        <p className="font-semibold">{s.user_name}</p>
                        <p className="text-xs text-[var(--muted-text)]">{s.filename} · {new Date(s.submitted_at).toLocaleString()}</p>
                        {s.graded && <p className="text-xs mt-0.5">Grade: <span className="font-bold">{s.grade}/100</span></p>}
                      </div>
                      <div className="flex gap-2">
                        <a href={downloadUrl(`/project-submissions/${s.id}/download`)} target="_blank" rel="noreferrer" className="nb-btn nb-btn-white text-xs"><Download className="w-3.5 h-3.5" /></a>
                        <button onClick={() => gradeSub(s.id, p.id)} className="nb-btn nb-btn-mint text-xs">{s.graded ? "Regrade" : "Grade"}</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ))}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setShowNew(false)}>
          <div className="nb-card-static p-7 w-full max-w-lg animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">New project</h2>
              <button onClick={() => setShowNew(false)} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input data-testid="project-title-input" required placeholder="Project title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nb-input" />
              <textarea data-testid="project-desc-input" required placeholder="Project description / instructions" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nb-input min-h-[120px]" />
              <input data-testid="project-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="nb-input" />
              {error && <div className="text-sm text-red-600">{error}</div>}
              <button data-testid="project-create-btn" disabled={busy} type="submit" className="nb-btn nb-btn-mint w-full">{busy ? "Creating..." : "Create project"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
